import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Badge from "@mui/material/Badge";
import EditIcon from "@mui/icons-material/ModeEdit";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
//Limitless imports
import ProfilePhoto from "../profile-photo/ProfilePhoto";
import Grid from "@limitless/modern/layout/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import CloseIcon from "@limitless/icons/Close";
import ButtonEditIcon from "@limitless/icons/Edit";
import DeleteIcon from "@limitless/icons/Delete";
import Button from "@limitless/modern/inputs/button/Button";
import { AddPicture, useExpertProfileContext } from "../../../../expert";
import { profilePlaceholderLight, profilePlaceholderDark } from "@limitless/images";
const EditProfilePhoto = ({
  profile,
  handleRemovePhoto,
  handleSavePhoto
}) => {
  const [showEditDialogue, setShowEditDialogue] = React.useState(false);
  const [isDelete, setIsDelete] = React.useState(false);
  const [isEdit, setIsEdit] = React.useState(false);
  const {
    isDarkMode
  } = useExpertProfileContext();
  const classes = useStyles();
  const profilePlaceHolder = !isDarkMode ? profilePlaceholderLight : profilePlaceholderDark;
  const avatarEditorRef = React.useRef();
  React.useEffect(() => {
    if (profile.picture === "") {
      setIsEdit(true);
    }
  }, [showEditDialogue]);
  const title = React.useMemo(() => isDelete ? "Delete profile picture" : "Update profile picture", [isDelete]);
  const leftButtonTitle = React.useMemo(() => isDelete || isEdit ? "Cancel" : "Change", [isDelete, isEdit]);
  const rightButtonTitle = React.useMemo(() => isEdit ? "Save" : "Delete", [isDelete, isEdit]);
  const handleCancelButtonClick = () => {
    if (isEdit) {
      setShowEditDialogue(false);
      setIsEdit(false);
    } else if (isDelete) {
      setShowEditDialogue(false);
      setIsDelete(false);
    } else {
      setIsEdit(true);
    }
  };
  const handleOkButtonClick = async () => {
    if (isDelete) {
      handleRemovePhoto();
      setShowEditDialogue(false);
      setIsDelete(false);
    } else if (isEdit) {
      const canvas = avatarEditorRef.current.getImageScaledToCanvas();
      const {
        getCompressCanvasData
      } = await import("@limitless/navigation");
      getCompressCanvasData(canvas, profileData => handleSavePhoto(profileData, () => handleCloseClick()));
    } else {
      setIsDelete(true);
    }
  };
  const handleCloseClick = () => {
    setShowEditDialogue(false);
    setIsDelete(false);
    setIsEdit(false);
  };
  const rendernDialogueContent = React.useMemo(() => /*#__PURE__*/React.createElement(React.Fragment, null, !isDelete && /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(2)
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "text.primary",
    variant: "caption"
  }, "Our customers love to see the friendly faces behind the answers. When you upload a photo, please make sure this is yours, and is appropriate for this purpose.")), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "center"
  }, isEdit ? /*#__PURE__*/React.createElement(AddPicture, {
    avatarEditorRef: avatarEditorRef
  }) : /*#__PURE__*/React.createElement(ProfilePhoto, {
    profile: profile,
    profilePicturePlaceholder: profilePlaceHolder,
    pictureStyle: classes.dialogueAvatar
  })), isDelete && /*#__PURE__*/React.createElement(Grid, {
    mt: theme => theme.spacing(2)
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "text.primary",
    variant: "caption"
  }, "Are you sure you want to delete your profile picture?")), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "space-between",
    pt: theme => theme.spacing(2)
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 5.7
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    variant: "outlined",
    startIcon: isDelete || isEdit ? null : /*#__PURE__*/React.createElement(ButtonEditIcon, {
      viewBox: "0 0 18 18"
    }),
    onClick: handleCancelButtonClick,
    size: "small"
  }, leftButtonTitle)), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 5.7
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    variant: isDelete || isEdit ? "contained" : "outlined",
    startIcon: isDelete || isEdit ? null : /*#__PURE__*/React.createElement(DeleteIcon, {
      viewBox: "0 0 18 18"
    }),
    onClick: handleOkButtonClick,
    size: "small"
  }, rightButtonTitle)))), [showEditDialogue, isDelete, isEdit]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Badge, {
    badgeContent: /*#__PURE__*/React.createElement(EditIcon, {
      className: classes.editIcon
    }),
    color: "primary",
    overlap: "circular",
    classes: {
      badge: classes.badgeStyle
    },
    onClick: () => setShowEditDialogue(true)
  }, /*#__PURE__*/React.createElement(ProfilePhoto, {
    profile: profile,
    profilePicturePlaceholder: profilePlaceHolder
  })), /*#__PURE__*/React.createElement(Dialog, {
    open: showEditDialogue,
    onClose: handleCloseClick,
    paperStyle: {
      className: classes.DialoguePaper
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    p: theme => theme.spacing(3)
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1"
  }, title), /*#__PURE__*/React.createElement(IconButton, {
    onClick: handleCloseClick,
    className: classes.closeButton
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    sx: {
      color: theme => theme.palette.grey[2]
    }
  }))), rendernDialogueContent)));
};
const useStyles = makeStyles(theme => ({
  badgeStyle: {
    height: 28,
    width: 28,
    borderRadius: "50%",
    cursor: "pointer",
    position: "absolute !important",
    left: 59,
    top: 14,
    backgroundColor: theme.palette.primary.main
  },
  editIcon: {
    height: 17,
    width: 17
  },
  dialogueAvatar: {
    height: 202,
    width: 202,
    marginTop: "16px"
  },
  DialoguePaper: {
    maxWidth: "364px",
    borderRadius: "12px"
  },
  closeButton: {
    height: 25,
    width: 25,
    marginRight: "-12px"
  }
}));
export default EditProfilePhoto;
EditProfilePhoto.propTypes = {
  profile: PropTypes.object.isRequired,
  handleRemovePhoto: PropTypes.func.isRequired,
  handleSavePhoto: PropTypes.func.isRequired
};